import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ALargeSmall, LucideAngularModule } from 'lucide-angular';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { NoEmojiDirective } from '../../../shared/helpers/noEmoji.directive';
import { Icons } from '../../../shared/helpers/icons';
import { EAssessmentSubTypes, EAssessmentTypes, ESectionRoutes, ESectionTypes  } from '../../../shared/models/eop_types.enum';
import { IContact, IUserInfo } from '../../../shared/models/contact';
import { UserDataService } from '../../../shared/services/userData.service';
import { HazardAnnexesService } from '../../../shared/services/hazardAnnexes.service';
import { ContactsService } from '../../../shared/services/contact.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';


@Component({
  selector: 'new-hazard-assessment',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    FormsModule,
    SpinnerComponent,
    NoEmojiDirective
  ],
  templateUrl: './new-hazard-assessment.component.html',
  styleUrl: './new-hazard-assessment.component.css'
})
export class NewHazardAssessmentComponent implements OnInit, OnDestroy {

  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<any>();

  eopSection: ESectionRoutes | null = null;
  icons = Icons;
  title: string = '';
  assessmentSubSection: string = "";
  contactsList: IContact[] = [];
  fullContactsList: IContact[] = [];
  selectedContactId: number = 0;
  routerSub!: Subscription;

  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  room_number: string = '';
  emails: { email: string; contact_id?: number }[] = [];
  currentAssessmentType: string | null = null;
  currentAssessmentSubType: string | null = null;
  currentPageRoute: string = '';  

  formSectionFields: Record<string, string[]> = {
    'AL-School Building': [
      'Long, unsupported roof spans',
      'Large, window panes (especially over exits)',
      'Heating and air conditioning units',
      'Overhangs',
      'Exit doors improperly marked',
      'Exit doors not opening to the outside',
      'Missing or malfunctioning panic hardware on exit doors',
      'Broken or nonfunctioning surveillance cameras and monitoring equipment',
      'Safety zones improperly marked in career technical shop',
      'Safety signs missing or illegible in career technical shop',
      'Missing, improper, or damaged safety guards on machines in career technical building or shops/work areas',
      'Safety eyewear available for all students operating career technical machinery or performing experiments in the science lab',
      'Outside fire escapes present for buildings with two or more stories (for buildings without fireproof stairs)',
      'Fire escape directional diagrams posted in all rooms that house students',
      'Unsecured fire extinguishers or fire extinguishers not properly charged and tagged',
      'Safe-place diagrams posted for severe weather in all rooms that house students',
      'Toxic, corrosive, or flammable materials not stored properly',
      'Inadequate ventilation systems in chemical storage areas',
      'Malfunctioning alarm system',
      'Hazardous materials located in areas without warning signs',
      'Unsecured appliances',
      'Unsecured filing cabinets or cabinets with inadequate drawer latches',
      'Inadequately supported light fixtures',
      'Unanchored table lamps',
      'Unsecured athletic equipment',
      'Other'
    ],
    'AL-Playground': [
      'Equipment in need of repair',
      'Rocks or other material that could cause injury',
      'Fences in need of repair',
      'Exposed nails, screws, or bolts',
      'Traffic pattern during the school day',
      'Schedule playground safety inspections on a regular basis',
      'Other'
    ],
    'AL-School Grounds': [
      'Trees or shrubs that present fire or wind hazards or provide areas for intruders to hide',
      'Streams in close proximity',
      'Electric wires',
      'Gasoline or propane tanks',
      'Natural gas lines',
      'Fences in need of repair',
      'Traffic pattern during the school day',
      'Insufficient lighting for activities in all areas of the campus',
      'Other'
    ],
    'AL-Other Structures': [
      'Unsecured portable structures',
      'Portable buildings properly anchored',
      'Unsecured siding or roofing materials',
      'Incompatible chemical storage',
      'Inadequate ventilation',
      'Other fire hazards (List)',
      'Other'
    ],
    'AL-Classroom': [
      'Free-standing cabinets, bookcases, and wall shelves',
      'Heavy objects on high shelves',
      'Aquariums and other potentially hazardous displays located near seating areas',
      'Unsecured TV monitors',
      'Unsecured wall-mounted objects',
      'Hanging plants above or near seating areas',
      'Incompatible chemicals stored in close proximity (e.g., window cleaner and ammonia)',
      'Paper or other combustibles (e.g., greasy rags) stored near heat source',
      'No safety glasses or shields in science/chemistry labs and vocational classrooms',
      'Floors not covered with nonskid wax or nonskid pads at each water-bearing workstation',
      'Lack of ground fault interrupters (GFI) within arms reach of faucets',
      'Inadequate or no fire blankets (non-asbestos) prominently labeled and strategically located in the lab',
      'Lack of or poorly functioning eyewash stations in the lab or vocational classroom',
      'Lack of or poorly functioning master shutoffs for gas and electric in the lab or vocational classroom',
      'Other'
    ],
    'AL-Evacuation Routes': [
      'Hallways and/or doors containing glass panels that are other than tempered glass or Plexiglas',
      'Lockers, bookshelves, or other storage units along hallways + Hallways may be cluttered with debris from ceilings, fallen light fixtures, broken glass, and toppled storage units. Students should be advised to anticipate these hazard',
      'Lighting that is dependent on electricity rather than sunlight (no emergency lighting)',
      'Elevators + Elevators are vulnerable to damage from fires, earthquakes, and other hazards. Signs should be posted near elevators prohibiting their use during emergencies',
      'Building exit routes that pass through canopies or porch-like structures',
      'Roofs with clay or slate tiles',
      'Building facings that include parapets, balconies, or cornices',
      'Gas, sewer, or power lines near the outdoor assembly area',
      'Other'
    ],
    'AL-Neighborhoods': [
      'Facilities containing toxic, chemically reactive, and/or radioactive materials. * Be sure to include both manufacturers and users (e.g., gas stations, storage facilities, pesticide manufacturers/distributors, nuclear power plants)',
      'High-voltage power lines and substations',
      'Transportation routes of vehicles carrying hazardous materials (e.g., truck routes and railroad rights of way)',
      'Underground utility vaults and above-the-ground transformers',
      'Multi-story buildings vulnerable to damage or collapse (e.g., unreinforced masonry construction)',
      'Water towers, tanks, and treatment centers',
      'Gas storage facilities',
      'Natural gas plants',
      'Laboratory and Agricultural Facilities',
      'Fireworks Factories/Storage Facilities',
      'Military Instillations/Munitions Factories/Depots',
      'Pharmaceutical Companies',
      'Government buildings and landmarks',
      'High crime rate areas (e.g., drugs, prostitution, theft)',
      'Other'
    ]
  };
  assessmentByRoute: Record<string, string[]> = {
    'hazard-school-ground': [
      'AL-School Building',
      'AL-Playground',
      'AL-School Grounds',
      'AL-Other Structures'
    ],
    'hazard-classroom': ['AL-Classroom'],
    'hazard-evacuation-routes': [ 'AL-Evacuation Routes' ],
    'hazard-neighborhoods': [ 'AL-Neighborhoods' ]
  };

  // Store form state per section
  sectionForms: {
    [section: string]: {
      formData: { [field: string]: boolean | string }; // comment now lives inside this
    };
  } = {};  

  loading: boolean = false;
  showContactsList: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userDataService: UserDataService,
    private hazardAnnexesService: HazardAnnexesService,
    private contactsService: ContactsService
  ) {}
  
  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    
    this.routerSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.initializeFromRoute());

    this.initializeFromRoute();

    // console.log('current route:', this.currentPageRoute);
    this.loadContacts();
  }

  ngOnDestroy() {
    this.routerSub?.unsubscribe();
  }

  initializeFromRoute() {
    this.currentPageRoute = this.route.snapshot.routeConfig?.path?.split('/').pop() || '';    
    if (this.currentPageRoute == 'hazard-school-ground') {
      this.currentAssessmentType = 'AL-School Grounds';
    } else if ( this.currentPageRoute == 'hazard-classroom') {
      this.currentAssessmentType = 'AL-Classroom';
    }  else if (this.currentPageRoute == 'hazard-evacuation-routes') {
      this.currentAssessmentType = 'AL-Evacuation Routes';
    } else if (this.currentPageRoute == 'hazard-neighborhoods') {
      this.currentAssessmentType = 'AL-Neighborhoods';
    }

    // Get ID from route
    this.eopSection = ESectionRoutes[this.currentPageRoute as keyof typeof ESectionRoutes];
    // Then get title from ESectionTypes using that ID
    this.title = String(ESectionTypes[this.eopSection as unknown as keyof typeof ESectionTypes]) || '';

    console.log('Resolved title:', this.title);

    const sections = this.assessmentByRoute[this.currentPageRoute] || [];
    if (sections.length > 0) {
      this.assessmentSubSection = sections[0];
    }
    this.sectionForms = {};

    // Create form objects for each section in this route
    sections.forEach(section => {
      const fields = this.formSectionFields[section] || [];
      const formData: Record<string, boolean | string> = {};
      fields.forEach(field => (formData[field] = false));
      formData['comments'] = '';
      this.sectionForms[section] = { formData };
    });
  }
  
  loadContacts(): void {
    this.loading = true;
    this.contactsService.getContacts(this.currentBuildingId!).subscribe({
      next: (response) => {
        this.loading = false;
        this.fullContactsList = response.data || [];
        // Check if user is an nside.io user
        if (this.currentUser?.email?.toLowerCase().endsWith('@nside.io')) {
          // Show full list
          this.contactsList = [...this.fullContactsList];
        } else {
          // Show only non-nside.io contacts
          this.contactsList = this.fullContactsList.filter(
            (c: any) => !c.email?.toLowerCase().endsWith('@nside.io')
          );
        }
        console.log('Contacts loaded successfully:', response);
        // Sort the list after retrieving contacts list.
        if (this.contactsList.length > 0) {
          this.contactsList.sort((a, b) => a.name.localeCompare(b.name));
          this.selectedContactId = this.contactsList[0].id;
        }
      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error loading contacts:', error.message);
      }
    });
  };  
  // When contact changes, load their assessment data if available
  onContactChange(contactId: number): void {
    if (!contactId) return;
    this.selectedContactId = contactId;
  }

  onSubAssessmentChange(subSection: string): void {
    this.assessmentSubSection = subSection;
  
    // Ensure section form exists for the selected sub-section
    if (!this.sectionForms[subSection]) {
      const fields = this.formSectionFields[subSection] || [];
      const formData: Record<string, boolean | string> = {};
      fields.forEach(field => (formData[field] = false));
      formData['comments'] = '';
      this.sectionForms[subSection] = { formData };
    }
  }
  

  saveNewAssessment(): void {
    if (this.currentBuildingId && this.currentAssessmentType) {
      this.loading = true;
      const section = this.assessmentSubSection;
      const form = this.sectionForms[section]?.formData || {};
        
      const result: any = {};
  
      for (const key in form) {
        if (key === 'comments') {
          const commentValue = form[key];
          if (typeof commentValue === 'string' && commentValue.trim()) {
            result[key] = commentValue;
          }
        } else if (form[key]) {
          result[key] = 'Yes';
        }
      } 

      if (this.currentAssessmentType === 'AL-Classroom' && this.room_number.trim()) {
        result['room_number'] = this.room_number.trim();
      }
      const payload = { 
        "contact_id": this.selectedContactId,
        "receiver": this.contactsList.find(c => c.id === this.selectedContactId)?.email || '',
        "response_data": result 
      };

      if (this.currentAssessmentType === 'AL-Classroom') {
        this.hazardAnnexesService.createALClassroomAssessment(this.currentBuildingId, payload).subscribe({
          next: (response) => {
            this.loading = false;
            if (!response.has_error) {
              this.onSave.emit({ type: 'success', message: `This occurred after saving ${(this.currentAssessmentType || '').replace('AL-', '')} Assessment.`, data: response.data });
            }
          },
          error: (error: any) => {
            this.loading = false;
            this.onSave.emit({ type: 'unsuccess', message: error.message });
          }
        })
      } else {
        this.hazardAnnexesService.updateALAssessment(this.currentBuildingId, String(EAssessmentTypes[this.currentAssessmentType as keyof typeof EAssessmentTypes]), String(EAssessmentSubTypes[section as keyof typeof EAssessmentSubTypes]), payload).subscribe({
          next: (response) => {
            this.loading = false;
            if (!response.has_error) {
              this.onSave.emit({ type: 'success', message: `This occurred after saving ${(this.currentAssessmentType || '').replace('AL-', '')} Assessment.`, data: response.data });
            }
          },
          error: (error: any) => {
            this.loading = false;
            this.onSave.emit({ type: 'unsuccess', message: error.message });
          }
        })
      }
    }
  }

  closeModal(): void {
    this.onClose.emit();
  }

}
